import { formatJobNumber, jobContactDuties } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { formatTime, money } from '../../lib/format.js'
import { useIsTablet } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { SendDocumentPopup, type SendAddressee } from '../job-card/SendDocumentPopup.js'
import { exTaxFromIncTax } from '../proposal/draft.js'
import type { Job } from '../job-card/types.js'
import { Badge, SkeletonRows, Spinner, humanise, type Tone } from '../ui.js'
import { FieldLayer, GroupLabel, cardClass, dockPrimaryClass, dockQuietClass } from './kit.js'

type Invoice = RouterOutputs['invoices']['byId']
type Material = RouterOutputs['catalog']['searchMaterials'][number]
type Method = 'CASH' | 'CARD' | 'BANK_TRANSFER'
type Mode = 'items' | 'pay' | 'paid'

/** An invoice line as `invoices.update` takes it. */
interface LineInput {
  description: string
  quantity: string
  unitPrice: number
  unitPriceIncTax: number | null
  unitCost: number
  discountBasisPoints: number
  taxRateId: string | null
  isOptional: boolean
  sortOrder: number
  materialId: string | null
}

/** A line on the sheet: what is sent, plus the rate it is drawn with. */
interface Item {
  key: string
  input: LineInput
  taxBasisPoints: number
}

/** The seed's GST 10%, for a custom item while the rates have not loaded. */
const SEED_GST = { taxRateId: 'taxrate_gst_default', basisPoints: 1000 }

const METHOD_WORD: Record<Method, string> = {
  CASH: 'cash',
  CARD: 'card',
  BANK_TRANSFER: 'transfer',
}

/**
 * The job's bill, the way a technician works it at the door: what is on it,
 * what else the job usually carries, the price list, and taking the money.
 *
 * It is the job's live invoice and nothing else. Adding the first item
 * raises a draft (`invoices.create`); every change after that replaces the
 * lines (`invoices.update`), the same write the office's billing tab makes.
 * Once money is against an invoice its lines are fixed and a credit note is
 * the office's instrument (decision 1), so the steppers go.
 *
 * **Taking payment is recording it.** There is no gateway, so Tap to Pay and
 * a payment link are drawn and switched off; cash, a card on the terminal
 * and a bank transfer are written through `payments.record`. A draft is
 * issued first, because the ledger refuses money against a draft. Sending a
 * receipt is emailing the invoice, and only for whoever may send one.
 */
export function FieldBilling({ job, onClose }: { job: Job; onClose: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const tablet = useIsTablet()

  const [mode, setMode] = useState<Mode>('items')
  const [q, setQ] = useState('')
  const [custom, setCustom] = useState<string | null>(null)
  const [draft, setDraft] = useState<Item[] | null>(null)
  const edits = useRef(0)
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<Method>('CASH')
  const [reference, setReference] = useState('')
  const [lastPaid, setLastPaid] = useState<{ amount: number; method: Method } | null>(null)
  const [sending, setSending] = useState(false)

  const live = job.invoices.find((invoice) => invoice.status !== 'VOID')
  const quote = job.quotes.find((row) => row.status !== 'SUPERSEDED')
  const invoiceQuery = useQuery({
    ...trpc.invoices.byId.queryOptions({ id: live?.id ?? '' }),
    enabled: Boolean(live),
  })
  const invoice = live ? invoiceQuery.data : undefined

  const taxRates = useQuery({ ...trpc.catalog.taxRates.queryOptions(), enabled: can('CATALOG_VIEW') })
  const suggestions = useQuery({
    ...trpc.catalog.linesForJob.queryOptions({ jobId: job.id }),
    enabled: can('QUOTE_VIEW'),
  })
  const catalog = useQuery({
    ...trpc.catalog.searchMaterials.queryOptions({ q: q.trim(), clientId: job.client.id, limit: 10 }),
    enabled: can('CATALOG_VIEW') && mode === 'items',
  })

  const refresh = async (invoiceId?: string) => {
    await queryClient.invalidateQueries(trpc.jobs.byId.queryFilter({ id: job.id }))
    if (invoiceId) await queryClient.invalidateQueries(trpc.invoices.byId.queryFilter({ id: invoiceId }))
  }

  const create = useMutation(trpc.invoices.create.mutationOptions())
  const update = useMutation(trpc.invoices.update.mutationOptions())
  const issue = useMutation(trpc.invoices.issue.mutationOptions())
  const record = useMutation(trpc.payments.record.mutationOptions())
  const note = useMutation(trpc.jobs.addNote.mutationOptions())

  const serverItems: Item[] = (invoice?.lines ?? []).map((line, index) => ({
    key: line.id,
    taxBasisPoints: line.taxRateBasisPoints,
    input: {
      description: line.description,
      quantity: quantityText(line.quantity),
      unitPrice: line.unitPrice,
      unitPriceIncTax: line.unitPriceIncTax ?? null,
      unitCost: line.unitCost ?? 0,
      discountBasisPoints: line.discountBasisPoints ?? 0,
      taxRateId: line.taxRateId ?? null,
      isOptional: false,
      sortOrder: line.sortOrder ?? index,
      materialId: line.materialId ?? null,
    },
  }))
  const items = draft ?? serverItems

  // Steppers are tapped in bursts. The lines go once the tapping stops, and
  // the draft stays on screen until the server's copy has caught up with it.
  useEffect(() => {
    if (draft === null || !invoice) return
    const version = edits.current
    const timer = window.setTimeout(() => {
      update.mutate(
        { id: invoice.id, lines: draft.map((item, index) => ({ ...item.input, sortOrder: index })) },
        {
          onSuccess: async () => {
            await refresh(invoice.id)
            if (edits.current === version) setDraft(null)
          },
        },
      )
    }, 500)
    return () => clearTimeout(timer)
  }, [draft, invoice?.id])

  const writable =
    can('INVOICE_EDIT') &&
    (!live ||
      (invoice !== undefined &&
        invoice.status !== 'VOID' &&
        invoice.status !== 'WRITTEN_OFF' &&
        invoice.amountPaid === 0))

  const change = (next: Item[]) => {
    edits.current += 1
    setDraft(next)

    if (live) return
    // The first item raises the invoice. Nothing else can be added until it
    // exists, so there is never a second draft racing the first.
    create.mutate(
      {
        clientId: job.client.id,
        jobId: job.id,
        discountAmount: 0,
        lines: next.map((item, index) => ({ ...item.input, sortOrder: index })),
      },
      {
        onSuccess: async (created) => {
          await refresh()
          await queryClient.fetchQuery(trpc.invoices.byId.queryOptions({ id: created.id }))
          setDraft(null)
        },
        onError: () => setDraft(null),
      },
    )
  }

  const rateOf = (taxRateId: string | null) =>
    taxRates.data?.find((rate) => rate.id === taxRateId)?.rateBasisPoints ?? 0

  const add = (input: Omit<LineInput, 'sortOrder'>, taxBasisPoints: number) => {
    const existing = input.materialId
      ? items.find((item) => item.input.materialId === input.materialId)
      : undefined
    if (existing) {
      bump(existing.key, Number(input.quantity) || 1)
      return
    }
    change([
      ...items,
      { key: `new-${Date.now()}`, taxBasisPoints, input: { ...input, sortOrder: items.length } },
    ])
  }

  const bump = (key: string, by: number) =>
    change(
      items.map((item) =>
        item.key === key
          ? { ...item, input: { ...item.input, quantity: String(Math.max(1, Number(item.input.quantity) + by)) } }
          : item,
      ),
    )

  const setPrice = (key: string, incTax: number) =>
    change(
      items.map((item) =>
        item.key === key
          ? {
              ...item,
              input: {
                ...item.input,
                unitPrice: exTaxFromIncTax(incTax, item.taxBasisPoints),
                unitPriceIncTax: incTax,
              },
            }
          : item,
      ),
    )

  const remove = (key: string) => change(items.filter((item) => item.key !== key))

  const defaultRate = taxRates.data?.find((rate) => rate.isDefault)
  const customTax = defaultRate
    ? { taxRateId: defaultRate.id, basisPoints: defaultRate.rateBasisPoints }
    : SEED_GST
  const addCustom = (description: string, incTax: number) => {
    add(
      {
        description,
        quantity: '1',
        unitPrice: exTaxFromIncTax(incTax, customTax.basisPoints),
        unitPriceIncTax: incTax,
        unitCost: 0,
        discountBasisPoints: 0,
        taxRateId: customTax.taxRateId,
        isOptional: false,
        materialId: null,
      },
      customTax.basisPoints,
    )
    setCustom(null)
  }

  const figures = totals(items, invoice?.discountAmount ?? 0)
  const total = draft === null && invoice ? invoice.totalIncTax : figures.total
  const gst = draft === null && invoice ? invoice.totalTax : figures.tax
  const paid = invoice?.amountPaid ?? 0
  const due = Math.max(0, total - paid)
  const saving = create.isPending || update.isPending || draft !== null

  const status: [string, Tone] =
    items.length === 0
      ? ['No items', 'neutral']
      : due === 0
        ? ['Paid', 'green']
        : paid > 0
          ? ['Part paid', 'amber']
          : invoice?.status === 'DRAFT' || !invoice
            ? ['Draft', 'neutral']
            : ['Unpaid', 'amber']

  const mayTake =
    can('PAYMENT_RECORD') &&
    invoice !== undefined &&
    due > 0 &&
    (invoice.status !== 'DRAFT' || can('INVOICE_ISSUE'))

  const cents = centsOf(amount)
  const amountOk = cents > 0 && cents <= due

  const takePayment = async () => {
    if (!invoice || !amountOk) return
    if (invoice.status === 'DRAFT') await issue.mutateAsync({ id: invoice.id })
    await record.mutateAsync({
      invoiceId: invoice.id,
      amount: cents,
      method,
      reference: method === 'BANK_TRANSFER' && reference.trim() !== '' ? reference.trim() : null,
    })
    await refresh(invoice.id)
    setLastPaid({ amount: cents, method })
    setReference('')
    setMode('paid')
  }

  const invoiceLater = () =>
    note.mutate(
      { jobId: job.id, body: `Invoice later: ${money(due)} for the office to invoice.` },
      {
        onSuccess: () => {
          void refresh()
          onClose()
        },
      },
    )

  const payError = issue.error ?? record.error
  const onJob = new Set(items.map((item) => item.input.materialId).filter(Boolean))
  const described = new Set(items.map((item) => item.input.description.trim().toLowerCase()))
  const suggested = (suggestions.data ?? []).flatMap((service) =>
    service.lines
      .filter((line) =>
        line.materialId ? !onJob.has(line.materialId) : !described.has(line.description.trim().toLowerCase()),
      )
      .map((line, index) => ({ key: `${service.jobServiceId}-${index}`, service: service.serviceName, line })),
  )
  const priceList = (catalog.data ?? [])
    .filter((material) => q.trim() !== '' || !onJob.has(material.id))
    .slice(0, q.trim() === '' ? 4 : 8)

  const title = mode === 'pay' ? 'Take payment' : mode === 'paid' ? 'Payment' : 'Billing'

  return (
    <FieldLayer
      title={title}
      subtitle={`${job.client.name} · ${formatJobNumber(job)}`}
      onClose={onClose}
      footer={
        mode === 'items' ? (
          due > 0 && invoice ? (
            <>
              {can('JOB_EDIT') ? (
                <button
                  type="button"
                  onClick={invoiceLater}
                  disabled={note.isPending || saving}
                  className={cn(dockQuietClass, 'flex-1')}
                >
                  Invoice later
                </button>
              ) : null}
              {mayTake ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setAmount((due / 100).toFixed(2))
                    setMode('pay')
                  }}
                  className={cn(dockPrimaryClass, 'flex-[2] tabular-nums')}
                >
                  Take payment · {money(due)}
                </button>
              ) : null}
            </>
          ) : (
            <button type="button" onClick={onClose} className={cn(dockPrimaryClass, 'flex-1 bg-brand-800 active:bg-brand-900')}>
              Done
            </button>
          )
        ) : mode === 'pay' ? (
          <>
            <button type="button" onClick={() => setMode('items')} className={cn(dockQuietClass, 'flex-1')}>
              Back
            </button>
            <button
              type="button"
              disabled={!amountOk || issue.isPending || record.isPending}
              onClick={() => void takePayment().catch(() => {})}
              className={cn(dockPrimaryClass, 'flex-[2] tabular-nums')}
            >
              {issue.isPending || record.isPending ? <Spinner className="size-5 text-white" /> : null}
              Record {money(cents)} {METHOD_WORD[method]}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setMode('items')}
            className={cn(dockPrimaryClass, 'flex-1 bg-brand-800 active:bg-brand-900')}
          >
            Back to billing
          </button>
        )
      }
    >
      {mode === 'items' ? (
        <div className="mx-auto flex w-full max-w-[680px] flex-col gap-[18px] px-4 pt-4 pb-7">
          {live && !invoice ? (
            <SkeletonRows rows={3} />
          ) : (
            <section>
              <GroupLabel aside={invoice ? `Invoice ${invoice.number}` : undefined}>On this job</GroupLabel>
              <div className={cn(cardClass, 'divide-y divide-ink-100')}>
                {items.map((item) => (
                  <div key={item.key} className="flex flex-col gap-2.5 py-3 pr-2.5 pl-3.5">
                    <div className="flex items-start gap-2">
                      <p className="min-w-0 flex-1 pt-1 text-[15px] leading-snug font-medium text-pretty text-ink-900">
                        {item.input.description}
                      </p>
                      {writable ? (
                        <button
                          type="button"
                          aria-label={`Remove ${item.input.description}`}
                          disabled={create.isPending}
                          onClick={() => remove(item.key)}
                          className="grid size-[30px] shrink-0 place-items-center rounded-full bg-ink-100 text-ink-500 active:bg-red-50 active:text-red-700"
                        >
                          <Icon name="close" className="size-3.5" strokeWidth={2.2} />
                        </button>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {writable ? (
                        <div className="flex h-9 shrink-0 items-center rounded-[10px] bg-white ring-1 ring-ink-300 ring-inset">
                          <button
                            type="button"
                            aria-label={`One less ${item.input.description}`}
                            disabled={Number(item.input.quantity) <= 1 || create.isPending}
                            onClick={() => bump(item.key, -1)}
                            className="h-full w-8 text-lg text-ink-800 disabled:opacity-30"
                          >
                            −
                          </button>
                          <span className="min-w-[22px] text-center text-[15px] font-semibold tabular-nums">
                            {item.input.quantity}
                          </span>
                          <button
                            type="button"
                            aria-label={`One more ${item.input.description}`}
                            disabled={create.isPending}
                            onClick={() => bump(item.key, 1)}
                            className="h-full w-8 text-lg text-ink-800 disabled:opacity-30"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <span className="shrink-0 text-[14px] text-ink-600 tabular-nums">× {item.input.quantity}</span>
                      )}
                      {writable ? (
                        <PriceField
                          item={item}
                          disabled={create.isPending}
                          onCommit={(incTax) => setPrice(item.key, incTax)}
                        />
                      ) : (
                        <span className="text-[13px] text-ink-500 tabular-nums">{money(unitInc(item))} each</span>
                      )}
                      <span className="ml-auto shrink-0 pr-1 text-right text-[15px] font-semibold tabular-nums">
                        {money(lineInc(item))}
                      </span>
                    </div>
                  </div>
                ))}
                {items.length === 0 ? (
                  <p className="px-3.5 py-[18px] text-center text-[14px] text-ink-500">
                    {writable ? 'No items. Add one from the price list below.' : 'Nothing billed on this job yet.'}
                  </p>
                ) : null}
              </div>
              {!writable && invoice && invoice.amountPaid > 0 ? (
                <p className="mt-1.5 px-1.5 text-[13px] text-ink-500">
                  Items are fixed once money has been taken. The office corrects them with a credit note.
                </p>
              ) : null}
              {!live && quote ? (
                <p className="mt-1.5 px-1.5 text-[13px] text-ink-500">
                  Quote {quote.number} is on this job for {money(quote.totalIncTax)}. Items added here go on
                  a new invoice.
                </p>
              ) : null}
              {create.error ?? update.error ? (
                <p role="alert" className="mt-1.5 px-1.5 text-[13px] text-red-700">
                  {errorMessage(create.error ?? update.error)}
                </p>
              ) : null}
            </section>
          )}

          {writable && suggested.length > 0 ? (
            <section>
              <GroupLabel>Suggested for this job</GroupLabel>
              <div className="flex flex-col gap-2">
                {suggested.map(({ key, service, line }) => {
                  const taxBasisPoints = rateOf(line.taxRateId)
                  const input = {
                    description: line.description,
                    quantity: line.quantity,
                    unitPrice: line.unitPrice,
                    unitPriceIncTax: line.unitPriceIncTax,
                    unitCost: 0,
                    discountBasisPoints: 0,
                    taxRateId: line.taxRateId,
                    isOptional: false,
                    materialId: line.materialId,
                  }
                  const quantity = Number(line.quantity) || 1
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-3 rounded-[14px] bg-brand-50 py-3 pr-3 pl-3.5 ring-1 ring-brand-100 ring-inset"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] leading-snug font-semibold text-brand-900">
                          {quantity > 1 ? `${quantity} × ` : ''}
                          {line.description}
                        </p>
                        <p className="mt-0.5 text-[13px] leading-snug text-brand-800">Usual for {service}</p>
                      </div>
                      <button
                        type="button"
                        disabled={create.isPending}
                        onClick={() => add(input, taxBasisPoints)}
                        className="h-9 shrink-0 rounded-full bg-brand-600 px-3 text-[14px] font-semibold whitespace-nowrap text-white tabular-nums disabled:opacity-50"
                      >
                        + {money(lineInc({ key, taxBasisPoints, input: { ...input, sortOrder: 0 } }))}
                      </button>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : null}

          {writable && can('CATALOG_VIEW') ? (
            <section>
              <GroupLabel>Add from price list</GroupLabel>
              <div className={cn(cardClass, 'divide-y divide-ink-100')}>
                <label className="flex h-[46px] items-center gap-2 px-3.5 text-ink-500">
                  <Icon name="search" className="size-[18px]" />
                  <input
                    type="search"
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    placeholder="Search services and products"
                    aria-label="Search services and products"
                    className="min-w-0 flex-1 bg-transparent text-base text-ink-900 outline-none placeholder:text-ink-400"
                  />
                </label>
                {priceList.map((material) => (
                  <button
                    key={material.id}
                    type="button"
                    disabled={create.isPending}
                    onClick={() => add(fromMaterial(material), material.taxRateBasisPoints)}
                    className="flex min-h-[50px] w-full items-center gap-3 py-1.5 pr-2.5 pl-3.5 text-left active:bg-ink-50 disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1 text-[15px] text-ink-900">{material.name}</span>
                    <span className="shrink-0 text-[14px] text-ink-600 tabular-nums">
                      {money(withTax(material.unitPrice, material.taxRateBasisPoints))}
                    </span>
                    <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-ink-100 text-brand-700">
                      <Icon name="plus" className="size-4" strokeWidth={2.2} />
                    </span>
                  </button>
                ))}
                {catalog.isSuccess && priceList.length === 0 ? (
                  <div className="px-3.5 py-3 text-[14px] text-ink-500">
                    <p>
                      {q.trim() ? `Nothing in the price list matches “${q.trim()}”.` : 'The price list is empty.'}
                    </p>
                    {q.trim() ? (
                      <button
                        type="button"
                        onClick={() => {
                          setCustom(q.trim())
                          setQ('')
                        }}
                        className="mt-1.5 font-semibold text-brand-700"
                      >
                        Add “{q.trim()}” as a custom item
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {writable ? (
            <section>
              {custom === null ? (
                <button
                  type="button"
                  onClick={() => setCustom('')}
                  className={cn(
                    cardClass,
                    'flex min-h-[50px] w-full items-center gap-3 px-3.5 text-left text-[15px] font-semibold text-brand-700 active:bg-ink-50',
                  )}
                >
                  <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-brand-50">
                    <Icon name="plus" className="size-4" strokeWidth={2.2} />
                  </span>
                  Add a custom item
                </button>
              ) : (
                <CustomItemForm
                  initial={custom}
                  disabled={create.isPending}
                  onAdd={addCustom}
                  onCancel={() => setCustom(null)}
                />
              )}
            </section>
          ) : null}

          <div className={cn(cardClass, 'divide-y divide-ink-100')}>
            <SumRow label="Total" value={money(total)} strong />
            <SumRow label="Includes GST" value={money(gst)} quiet />
            {paid > 0 ? <SumRow label="Paid" value={`− ${money(paid)}`} tone="green" /> : null}
            <div className="flex min-h-14 items-center justify-between px-3.5">
              <span className="text-[17px] font-bold text-ink-900">Balance due</span>
              <span
                className={cn(
                  'text-[22px] font-bold tabular-nums',
                  due > 0 || items.length === 0 ? 'text-ink-900' : 'text-emerald-700',
                )}
              >
                {money(due)}
              </span>
            </div>
          </div>

          {invoice && invoice.payments.length > 0 ? (
            <section>
              <GroupLabel>Payments</GroupLabel>
              <div className={cn(cardClass, 'divide-y divide-ink-100')}>
                {invoice.payments.map((payment) => (
                  <div key={payment.id} className="flex min-h-14 items-center gap-3 px-3.5 py-2">
                    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-700">
                      <Icon name={methodIcon(payment.method)} className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-ink-900">{humanise(payment.method)}</p>
                      <p className="text-[12px] text-ink-500">
                        {whenPaid(payment.receivedAt)}
                        {payment.reference ? ` · ref ${payment.reference}` : ''}
                      </p>
                    </div>
                    <span className="shrink-0 text-[15px] font-semibold text-emerald-700 tabular-nums">
                      {money(payment.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {invoice ? (
            <div className="flex items-center gap-2 px-1.5 text-[13px] text-ink-500">
              <Badge tone={status[1]} className="rounded-md px-2 py-0.5 text-xs">
                {status[0]}
              </Badge>
              {invoice.status === 'DRAFT'
                ? 'Not issued yet. Taking payment issues it.'
                : `Invoice ${invoice.number}, ${humanise(invoice.status).toLowerCase()}`}
              {saving ? <Spinner className="ml-auto size-3.5 text-ink-400" /> : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'pay' ? (
        <div className="mx-auto flex w-full max-w-[680px] flex-col gap-[18px] px-4 pt-4 pb-7">
          <div className={cn(cardClass, 'p-4')}>
            <p className="text-[12px] font-semibold tracking-[0.05em] text-ink-500 uppercase">Amount</p>
            <label className="mt-1.5 flex items-baseline gap-0.5">
              <span className="text-[32px] font-semibold text-ink-500">$</span>
              <input
                aria-label="Amount"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ''))}
                className="min-w-0 flex-1 bg-transparent p-0 text-[40px] font-bold tracking-[-0.02em] text-ink-900 tabular-nums outline-none"
              />
            </label>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {[
                [`Full balance ${money(due)}`, due],
                [`Half, ${money(Math.round(due / 2))}`, Math.round(due / 2)],
              ].map(([label, value]) => (
                <button
                  key={label}
                  type="button"
                  aria-pressed={cents === value}
                  onClick={() => setAmount((Number(value) / 100).toFixed(2))}
                  className={cn(
                    'min-h-9 rounded-full px-3 text-[14px] font-medium tabular-nums ring-1 ring-inset',
                    cents === value ? 'bg-brand-800 text-white ring-brand-800' : 'bg-white text-ink-700 ring-ink-300',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {cents > due ? (
              <p className="mt-2 text-[13px] text-red-700">That is more than the {money(due)} owing.</p>
            ) : null}
          </div>

          <section>
            <GroupLabel>How are they paying?</GroupLabel>
            <div role="radiogroup" aria-label="How are they paying?" className={cn(cardClass, 'divide-y divide-ink-100')}>
              <MethodRow
                icon="tap"
                label={`Tap to Pay on ${tablet ? 'iPad' : 'iPhone'}`}
                detail="Not connected yet. Use the card terminal."
                disabled
              />
              <MethodRow icon="link" label="Send a payment link" detail="Not connected yet." disabled />
              <MethodRow
                icon="cash"
                label="Cash"
                detail="Record cash taken on site"
                checked={method === 'CASH'}
                onPick={() => setMethod('CASH')}
              />
              <MethodRow
                icon="card"
                label="Card on the terminal"
                detail="Record a card payment taken on the EFTPOS machine"
                checked={method === 'CARD'}
                onPick={() => setMethod('CARD')}
              />
              <MethodRow
                icon="bank"
                label="Bank transfer"
                detail="Record a transfer the client has already made"
                checked={method === 'BANK_TRANSFER'}
                onPick={() => setMethod('BANK_TRANSFER')}
              />
            </div>
          </section>

          {method === 'BANK_TRANSFER' ? (
            <input
              aria-label="Transfer reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Transfer reference (optional)"
              className="h-12 rounded-[14px] bg-white px-3.5 text-base text-ink-900 ring-1 ring-ink-200 outline-none ring-inset placeholder:text-ink-400"
            />
          ) : null}

          {invoice?.status === 'DRAFT' ? (
            <p className="px-1.5 text-[13px] text-ink-500">
              Invoice {invoice.number} is issued when you record this, so the payment has a document to sit
              against.
            </p>
          ) : null}
          {payError ? (
            <p role="alert" className="px-1.5 text-[13px] text-red-700">
              {errorMessage(payError)}
            </p>
          ) : null}
        </div>
      ) : null}

      {mode === 'paid' && lastPaid ? (
        <div className="flex flex-col items-center gap-2.5 px-6 py-16 text-center">
          <span className="grid size-16 place-items-center rounded-full bg-emerald-50 text-emerald-700">
            <Icon name="check" className="size-8" strokeWidth={2} />
          </span>
          <p className="mt-1.5 text-[28px] font-bold text-ink-900 tabular-nums">{money(lastPaid.amount)} received</p>
          <p className="text-[15px] text-ink-500">
            {humanise(lastPaid.method)} · {job.client.name} ·{' '}
            {due > 0 ? `${money(due)} still owing` : 'paid in full'}
          </p>
          {invoice && can('INVOICE_SEND') ? (
            <button
              type="button"
              onClick={() => setSending(true)}
              className="mt-3.5 h-12 rounded-xl bg-white px-5 text-base font-semibold text-ink-800 ring-1 ring-ink-300 ring-inset"
            >
              Send receipt
            </button>
          ) : null}
        </div>
      ) : null}

      {invoice && sending ? (
        <SendDocumentPopup
          kind="invoice"
          id={invoice.id}
          number={invoice.number}
          addressee={addresseeOf(job)}
          open
          onClose={() => setSending(false)}
        />
      ) : null}
    </FieldLayer>
  )
}

function MethodRow({
  icon,
  label,
  detail,
  checked = false,
  disabled = false,
  onPick,
}: {
  icon: IconName
  label: string
  detail: string
  checked?: boolean
  disabled?: boolean
  onPick?: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex min-h-[62px] w-full items-center gap-3 px-3.5 py-2 text-left disabled:opacity-45',
        checked ? 'bg-brand-50/60' : 'bg-white',
      )}
    >
      <span
        className={cn(
          'grid size-[38px] shrink-0 place-items-center rounded-[10px]',
          checked ? 'bg-brand-800 text-brand-200' : 'bg-ink-100 text-ink-700',
        )}
      >
        <Icon name={icon} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold text-ink-900">{label}</span>
        <span className="block text-[13px] leading-snug text-ink-500">{detail}</span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'size-[22px] shrink-0 rounded-full',
          checked ? 'border-[7px] border-brand-600' : 'border-[1.5px] border-ink-300',
        )}
      />
    </button>
  )
}

/**
 * A line's price each, typed inc GST. The text is the field's own while it
 * has focus and goes to the lines on blur or Enter; a blank or unreadable
 * figure puts the old price back rather than billing nothing.
 */
function PriceField({
  item,
  disabled,
  onCommit,
}: {
  item: Item
  disabled: boolean
  onCommit: (incTax: number) => void
}) {
  const [text, setText] = useState<string | null>(null)
  const current = unitInc(item)

  const commit = () => {
    if (text === null) return
    const trimmed = text.trim()
    const value = Number(trimmed)
    if (trimmed !== '' && Number.isFinite(value) && value >= 0) {
      const cents = Math.round(value * 100)
      if (cents !== current) onCommit(cents)
    }
    setText(null)
  }

  return (
    <label className="flex h-9 min-w-0 items-center gap-0.5 rounded-[10px] bg-white pr-2.5 pl-2.5 ring-1 ring-ink-300 ring-inset focus-within:ring-2 focus-within:ring-brand-600">
      <span className="text-[15px] text-ink-500">$</span>
      <input
        aria-label={`Price each for ${item.input.description}`}
        inputMode="decimal"
        enterKeyHint="done"
        disabled={disabled}
        value={text ?? (current / 100).toFixed(2)}
        onFocus={(event) => {
          setText((current / 100).toFixed(2))
          event.currentTarget.select()
        }}
        onChange={(event) => setText(event.target.value.replace(/[^0-9.]/g, ''))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
        }}
        className="w-[68px] min-w-0 bg-transparent p-0 text-base font-medium text-ink-900 tabular-nums outline-none"
      />
      <span className="text-[12px] text-ink-400">each</span>
    </label>
  )
}

/** Something that is not in the price list: a description and a price inc GST. */
function CustomItemForm({
  initial,
  disabled,
  onAdd,
  onCancel,
}: {
  initial: string
  disabled: boolean
  onAdd: (description: string, incTax: number) => void
  onCancel: () => void
}) {
  const [description, setDescription] = useState(initial)
  const [price, setPrice] = useState('')
  const value = Number(price.trim())
  const cents = price.trim() !== '' && Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null
  const ready = description.trim() !== '' && cents !== null

  return (
    <form
      aria-label="Custom item"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready) onAdd(description.trim(), cents)
      }}
      className={cn(cardClass, 'flex flex-col gap-2.5 p-3.5')}
    >
      <p className="text-[13px] font-semibold tracking-[0.04em] text-ink-500 uppercase">Custom item</p>
      <input
        aria-label="Description"
        autoFocus={initial === ''}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="What is it? e.g. Extra wasp nest removal"
        className="h-11 rounded-[12px] bg-white px-3 text-base text-ink-900 ring-1 ring-ink-300 outline-none ring-inset placeholder:text-ink-400 focus:ring-2 focus:ring-brand-600"
      />
      <label className="flex h-11 items-center gap-1 rounded-[12px] bg-white px-3 ring-1 ring-ink-300 ring-inset focus-within:ring-2 focus-within:ring-brand-600">
        <span className="text-base text-ink-500">$</span>
        <input
          aria-label="Price inc GST"
          inputMode="decimal"
          autoFocus={initial !== ''}
          value={price}
          onChange={(event) => setPrice(event.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.00"
          className="min-w-0 flex-1 bg-transparent p-0 text-base text-ink-900 tabular-nums outline-none placeholder:text-ink-400"
        />
        <span className="text-[13px] text-ink-400">inc GST</span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-11 flex-1 rounded-xl bg-white text-[15px] font-semibold text-ink-700 ring-1 ring-ink-300 ring-inset"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!ready || disabled}
          className="h-11 flex-[2] rounded-xl bg-brand-600 text-[15px] font-semibold text-white tabular-nums disabled:opacity-40"
        >
          {cents !== null && cents > 0 ? `Add · ${money(cents)}` : 'Add item'}
        </button>
      </div>
    </form>
  )
}

function SumRow({
  label,
  value,
  strong,
  quiet,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  quiet?: boolean
  tone?: 'green'
}) {
  return (
    <div className="flex min-h-11 items-center justify-between gap-3 px-3.5">
      <span className={cn(quiet ? 'text-[13px] text-ink-500' : 'text-ink-600', tone === 'green' && 'text-emerald-700')}>
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          quiet ? 'text-[13px] text-ink-500' : 'text-ink-900',
          strong && 'font-semibold',
          tone === 'green' && 'font-semibold text-emerald-700',
        )}
      >
        {value}
      </span>
    </div>
  )
}

function fromMaterial(material: Material): Omit<LineInput, 'sortOrder'> {
  return {
    description: material.name,
    quantity: '1',
    unitPrice: material.unitPrice,
    unitPriceIncTax: null,
    unitCost: material.unitCost,
    discountBasisPoints: 0,
    taxRateId: material.taxRateId,
    isOptional: false,
    materialId: material.id,
  }
}

/** Who a receipt offers to go to: the job's people with an email, billing first. */
function addresseeOf(job: Job): SendAddressee {
  const billing = jobContactDuties(job.contacts).billing
  return {
    recipients: job.contacts.flatMap((link) =>
      link.contact.email
        ? [
            {
              name: [link.contact.firstName, link.contact.lastName].filter(Boolean).join(' '),
              email: link.contact.email,
            },
          ]
        : [],
    ),
    fallbackTo: billing?.contact.email ?? job.client.email,
    jobId: job.id,
  }
}

/** Quantity is a Decimal on the way in, and 2.000 is a 2 to the person counting. */
function quantityText(quantity: unknown): string {
  const value = Number(String(quantity))
  return Number.isFinite(value) ? String(value) : String(quantity)
}

function withTax(exTax: number, basisPoints: number): number {
  return exTax + Math.round((exTax * basisPoints) / 10_000)
}

function unitInc(item: Item): number {
  return item.input.unitPriceIncTax ?? withTax(item.input.unitPrice, item.taxBasisPoints)
}

/**
 * A line's amount while it is being changed, in integers. The server prices
 * it again on save with `money.calculateLine` and its figure replaces this.
 */
function lineParts(item: Item): { total: number; tax: number } {
  const { input, taxBasisPoints: rate } = item
  const quantity = Number(input.quantity)
  if (!Number.isFinite(quantity)) return { total: 0, tax: 0 }
  const scaled = Math.round(quantity * 10_000)
  const off = (value: number) => value - Math.round((value * input.discountBasisPoints) / 10_000)

  if (input.unitPriceIncTax != null) {
    const total = off(Math.round((scaled * input.unitPriceIncTax) / 10_000))
    return { total, tax: total - Math.round((total * 10_000) / (10_000 + rate)) }
  }
  const ex = off(Math.round((scaled * input.unitPrice) / 10_000))
  const tax = Math.round((ex * rate) / 10_000)
  return { total: ex + tax, tax }
}

function lineInc(item: Item): number {
  return lineParts(item).total
}

function totals(items: Item[], discount: number): { total: number; tax: number } {
  const sum = items.reduce(
    (acc, item) => {
      const parts = lineParts(item)
      return { total: acc.total + parts.total, tax: acc.tax + parts.tax }
    },
    { total: 0, tax: 0 },
  )
  return { total: Math.max(0, sum.total - discount), tax: sum.tax }
}

/** Dollars as typed to integer minor units. Never a float in the payload. */
function centsOf(dollars: string): number {
  const value = Number(dollars.trim())
  return Number.isFinite(value) ? Math.round(value * 100) : 0
}

function methodIcon(method: string): IconName {
  if (method === 'CASH') return 'cash'
  if (method === 'BANK_TRANSFER') return 'bank'
  return 'card'
}

function whenPaid(value: Date | string): string {
  const date = new Date(value)
  return date.toDateString() === new Date().toDateString()
    ? `Today, ${formatTime(date)}`
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}
